/* eslint-disable @typescript-eslint/no-explicit-any */
import { Async, F1 } from '@client/new/helpers/types';
import { SubscriptionType } from '../../collection/domain';
import { DiscountsModel } from '../model/Discounts.model';
import { PricesVM } from './newDiscount.mappers';
import { NewDiscount } from './newDiscount.viewModel';
import { ensureRange } from './utils';

type TransformerParams<T extends keyof NewDiscount> = {
  key: T;
  value: any;
  model: DiscountsModel;
  values: NewDiscount;
  prices: Async<PricesVM>;
};

type Transformer<
  T extends keyof NewDiscount = keyof NewDiscount,
  TR extends string | number | Partial<NewDiscount> =
    | string
    | number
    | Partial<NewDiscount>,
> = F1<TransformerParams<T>, TR>;

export const transformValue: Transformer<
  keyof NewDiscount,
  Partial<NewDiscount>
> = (p) => {
  const delta = transformations[p.key]?.(p as any);

  if (!delta) return { [p.key]: p.value };

  return {
    [p.key]: p.value,
    ...(typeof delta !== 'object' ? { [p.key]: delta } : delta),
  };
};

const transformSubscriptionType: Transformer<'subscriptionType'> = (p) => {
  const res: Partial<NewDiscount> = {};
  if (!p.model.subscriptionMap[p.value as SubscriptionType]) {
    res.subscriptionType = undefined;
    //toast.error('This subscription is not available (no price)');
  }
  if (p.value !== p.values.subscriptionType) {
    res.discountFixedValue = 0.01;
    res.discountPercentValue = 0.01;
  }
  return res;
};

const transformFixedValue: Transformer<'discountFixedValue'> = (p) => {
  if (p.prices.type !== 'fetched') return p.value;
  const { priceFloat } = p.prices.value;
  const isBundleCreator = p.model.creatorType === 'bundle';
  if (p.values.subscriptionType === 'monthly' || p.values.salesType === 'otp')
    return ensureRange(p.value, [0.01, priceFloat]);
  return ensureRange(p.value, [
    0.01,
    isBundleCreator ? priceFloat : priceFloat * 0.3,
  ]);
};

const transformPercentValue: Transformer<'discountPercentValue'> = (p) => {
  if (p.prices.type !== 'fetched') return p.value;
  const isBundleCreator = p.model.creatorType === 'bundle';
  if (p.values.subscriptionType === 'monthly' || p.values.salesType === 'otp')
    return ensureRange(p.value, [0.01, 100]);
  return ensureRange(p.value, [0.01, isBundleCreator ? 100 : 30]);
};

const transformDiscountCode: Transformer<'discountCode'> = (p) =>
  (p.value || '')
    .slice(0, 12)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const transformations: {
  [k in keyof NewDiscount]?: Transformer<k>;
} = {
  name: (p) => p.value.slice(0, 30),
  discountCode: transformDiscountCode,
  expirationMinute: (p) => ensureRange(p.value, [0, 59]),
  expirationHour: (p) => ensureRange(p.value, [0, 11]),
  redemptionsLimit: (p) => ensureRange(p.value, [1, 10000]),
  subscriptionType: transformSubscriptionType,
  discountFixedValue: transformFixedValue,
  discountPercentValue: transformPercentValue,
};

/* Spec:
price.type: Normal (!Exclusive)
  discountType: FixedAmount
    subscriptionType: monthly
      discountFixedValue: 0 < X <= monthly price
    subscriptionType: annual
      discountFixedValue: 0 < X <= 30% of the annual price

  discountType: Percentage
    subscriptionType: monthly
      discountPercentValue: 0 < X <= 100%
    subscriptionType: annual
      discountPercentValue: 0 < X <= 30%

price.type: PPC
  discountType: FixedAmount || Percentage
    subscriptionType: monthly || annual
      discountFixedValue: 0 < X <= price
      discountPercentValue: 0 < X <= 100%
*/

export const otpSelectionMapper = (value: {
  value: string;
  label: string;
  type: string;
}) => ({
  otpId: value.value,
  otpType: value.type === 'OFFER' ? 'bundle' : 'program',
  subscriptionType: null,
});
